<template>
  <!-- 省エネレポート詳細（期間指定） -->
  <div class="fuel-consumption-detail fuel-consumption-detail-voyage">
    <vx-card class="p-0 m-0" :key="detailCardKey">
      <!-- ヘッダー -->
      <div class="header block sm:flex sm:items-center sm:justify-between mb-2">
        <!-- 左 -->
        <div class="vessel-name-text mr-4">
          <div class="font-semibold">{{ vesselName }}</div>
        </div>

        <!-- 右 -->
        <div class="header-right-area flex items-center justify-end no-print">
          <!-- 次の航路 -->
          <div
            class="menu-button"
            @click="clickNextVoyageButton()"
            v-show="!isLastIndex"
          >
            <div class="flex items-center">
              <feather-icon
                icon="ChevronLeftIcon"
                svgClasses="h-6 w-6"
                class="cursor-pointer"
              ></feather-icon>
              <span class="mx-2">{{ nextRouteText }}</span>
            </div>
          </div>

          <!-- 次の航路(非活性) -->
          <div class="menu-button__disabled" v-show="isLastIndex">
            <div class="flex items-center">
              <feather-icon
                icon="ChevronLeftIcon"
                svgClasses="h-6 w-6"
              ></feather-icon>
              <span class="mx-2">{{ nextRouteText }}</span>
            </div>
          </div>

          <div class="vertical-divider h-6 ml-2"></div>

          <!-- 前の航路 -->
          <div
            class="menu-button"
            @click="clickPrevVoyageButton()"
            v-show="!isFirstIndex"
          >
            <div class="flex items-center">
              <span class="mx-2">{{ prevRouteText }}</span>
              <feather-icon
                icon="ChevronRightIcon"
                svgClasses="h-6 w-6"
                class="cursor-pointer"
              ></feather-icon>
            </div>
          </div>

          <!-- 前の航路(非活性) -->
          <div class="menu-button__disabled" v-show="isFirstIndex">
            <div class="flex items-center">
              <span class="mx-2">{{ prevRouteText }}</span>
              <feather-icon
                icon="ChevronRightIcon"
                svgClasses="h-6 w-6"
              ></feather-icon>
            </div>
          </div>

          <!-- メニュー -->
          <vs-dropdown
            vs-trigger-click
            class="cursor-pointer lg:hidden"
            :color="AISEA_COLOR.GRAY"
          >
            <div class="menu-button">
              <feather-icon
                icon="MoreHorizontalIcon"
                svgClasses="h-6 w-6"
              ></feather-icon>
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item class="w-48 p-1">
                <div @click="clickPeriodButton()">
                  <div class="flex items-center">
                    <img :src="periodButtonIconSrc" class="w-6 h-6" />
                    <span class="ml-2">月・年次の解析</span>
                  </div>
                </div>
              </vs-dropdown-item>
              <vs-dropdown-item class="w-48 p-1">
                <div @click="clickExportButton()">
                  <div class="flex items-center">
                    <img :src="exportButtonIconSrc" class="w-6 h-6" />
                    <span class="ml-2">PDF印刷</span>
                  </div>
                </div>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>

          <div class="hidden lg:flex lg:items-center">
            <div class="menu-button" @click="clickPeriodButton()">
              <div class="flex items-center">
                <img :src="periodButtonIconSrc" />
                <span class="ml-2">月・年次の解析</span>
              </div>
            </div>
            <div class="menu-button" @click="clickExportButton()">
              <div class="flex items-center">
                <img :src="exportButtonIconSrc" />
                <span class="ml-2">PDF印刷</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="content-area xl:flex justify-between">
        <!-- 左上 -->
        <div class="content-left-area w-full xl:w-5/12 xl:pr-4">
          <div class="period-area mb-4">
            <p class="mb-4">
              <span>期間：</span>
              <span class="font-semibold">{{ reportPeriodText }} </span>
            </p>
          </div>

          <!-- カードコンポーネント群 -->
          <panel-card-list class="mb-4 xl:mb-0 print-break" />
        </div>
        <!-- 右上 -->
        <div
          class="content-right-area w-full xl:w-7/12 xl:content-between print-break"
        >
          <div class="map-view">
            <vessel-live-map
              ref="VesselLiveMap"
              :showMap="showMap"
            ></vessel-live-map>
          </div>
          <div class="h-2/6">
            <weather-routing-result-panel></weather-routing-result-panel>
          </div>
        </div>
      </div>
      <div class="tab-container mt-4 flex items-start justify-between no-print">
        <vs-tabs
          :color="AISEA_COLOR.DARK_BLUE"
          class="no-print"
          v-model="selectedTab"
        >
          <vs-tab
            class="tabs-shadow-none"
            v-for="item in tabOptions"
            :key="item.id"
            :label="item.label"
          ></vs-tab>
          <div v-if="isMonthly">
            <chart-fuel-consumption-for-month
              ref="chartFuelConsumptionForMonth"
              v-show="selectedTab === 0"
            ></chart-fuel-consumption-for-month>
          </div>
          <div v-else>
            <chart-fuel-consumption-for-year
              ref="chartFuelConsumptionForYear"
              v-show="selectedTab === 0"
            ></chart-fuel-consumption-for-year>
          </div>
        </vs-tabs>
      </div>
      <div class="print">
        <section class="print-break">
          <h1>燃料消費量</h1>
          <div v-if="isMonthly">
            <chart-fuel-consumption-for-month
              ref="chartFuelConsumptionForMonth"
            ></chart-fuel-consumption-for-month>
          </div>
          <div v-else>
            <chart-fuel-consumption-for-year
              ref="chartFuelConsumptionForYear"
            ></chart-fuel-consumption-for-year>
          </div>
        </section>
      </div>
    </vx-card>
    <fuel-consumption-period-dialog ref="FuelConsumptionPeriodDialog" />
  </div>
</template>

<script>
import VesselLiveMap from "./components/VesselLiveMap.vue";
import PanelCardList from "./components/PanelCardList.vue";
import { createNamespacedHelpers } from "vuex";
const {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} = createNamespacedHelpers("fuelConsumption");
import moment from "moment";
import FuelConsumptionPeriodDialog from "../../fuel-consumption-vessel-list/components/FuelConsumptionPeriodDialog.vue";
import { ColorMixin } from "@/mixins";
import {
  ChartFuelConsumptionForMonth,
  ChartFuelConsumptionForYear,
} from "./components";
import WeatherRoutingResultPanel from "@/views/enterprise/addon/fuel-consumption/fuel-consumption-detail/period/components/WeatherRoutingResultPanel.vue";

export default {
  components: {
    WeatherRoutingResultPanel,
    ChartFuelConsumptionForMonth,
    ChartFuelConsumptionForYear,
    VesselLiveMap,
    PanelCardList,
    FuelConsumptionPeriodDialog,
  },
  mixins: [ColorMixin],
  data() {
    return {
      periodButtonIconSrc: require("@/assets/images/fuel-consumption/icon/calendar.svg"),
      exportButtonIconSrc: require("@/assets/images/fuel-consumption/icon/document.svg"),
      showMap: true,
      detailCardKey: 1,
      reportPeriodList: [],
      voyagePeriod: { startDate: null, endDate: null },
      selectedTab: 0,
      tabOptions: [
        {
          label: "燃料消費量",
        },
      ],
    };
  },

  watch: {
    year(val) {
      this.reload();
    },

    month(val) {
      this.reload();
    },
  },

  computed: {
    ...mapState(["vessels"]),
    ...mapGetters([
      "getUsualRoute",
      "getRecomendedRoute",
      "getActualRoute",
      "getRouteAgreementRate",
      "getMapCenterPosition",
      "getPrevVoyageId",
      "getNextVoyageId",
      "getIsFirstIndex",
      "getIsLastIndex",
      "getVessel",
    ]),
    vessel() {
      return this.getVessel(this.vesselId);
    },
    vesselName() {
      if (!this.vessel) return "";
      return this.vessel.name;
    },
    prevRouteText() {
      return this.isMonthly ? "前月" : "前年";
    },
    nextRouteText() {
      return this.isMonthly ? "翌月" : "翌年";
    },
    isMonthly() {
      return "month" in this.$route.params;
    },
    range() {
      return this.isMonthly ? "月間" : "年間";
    },
    reportPeriodText() {
      return this.isMonthly
        ? `${this.$route.params.year}年${this.$route.params.month}月`
        : `${this.$route.params.year}年`;
    },
    vesselId() {
      return this.$route.params.vesselId;
    },
    year() {
      return this.$route.params.year;
    },
    month() {
      return this.$route.params.month;
    },
    isFirstIndex() {
      const format = this.isMonthly ? "YYYYMM" : "YYYY";
      const current = this.isMonthly
        ? `${this.year}${this.month}`
        : `${this.year}`;
      return moment(this.voyagePeriod.startDate).format(format) === current;
    },
    isLastIndex() {
      const format = this.isMonthly ? "YYYYMM" : "YYYY";
      const current = this.isMonthly
        ? `${this.year}${this.month}`
        : `${this.year}`;
      return moment(this.voyagePeriod.endDate).format(format) === current;
    },
  },

  methods: {
    ...mapActions([
      "fetchVoyageList",
      "fetchVoyagePeriod",
      "fetchVoyageRecordsPeriod",
      "fetchAllVessels",
    ]),
    ...mapMutations(["RESET_DETAIL_DATA"]),
    clickPrevVoyageButton() {
      const params = { vesselId: this.vesselId };
      const rangeText = this.isMonthly ? "months" : "years";
      const prevMoment = moment(`${this.year}-${this.month}`).subtract(
        1,
        rangeText
      );
      params.year = prevMoment.format("YYYY");

      if (this.isMonthly) {
        params.month = prevMoment.format("MM");
      }

      this.$router.push({
        name: this.$route.name,
        params: params,
      });
    },
    clickNextVoyageButton() {
      const params = { vesselId: this.vesselId };
      const rangeText = this.isMonthly ? "months" : "years";
      const nextMoment = moment(`${this.year}-${this.month}`).add(1, rangeText);
      params.year = nextMoment.format("YYYY");

      if (this.isMonthly) {
        params.month = nextMoment.format("MM");
      }

      this.$router.push({
        name: this.$route.name,
        params: params,
      });
    },
    clickPeriodButton() {
      this.$refs.FuelConsumptionPeriodDialog.show(this.vesselId);
    },
    clickExportButton() {
      if (this.isMonthly) {
        this.$refs.chartFuelConsumptionForMonth.print();
      } else {
        this.$refs.chartFuelConsumptionForYear.print();
      }
      window.print();
    },
    setShowMap(showMap) {
      this.showMap = showMap;
    },
    async initVoyagePeriods() {
      this.voyagePeriod = await this.fetchVoyagePeriod(
        this.$route.params.vesselId
      );
    },

    fetchReportPeriodList() {
      const listLength = moment(this.endDate).diff(this.startDate, "month") + 1;
      const list = [];
      for (let i = 0; i < listLength; i++) {
        const obj = {
          year: moment(this.endDate)
            .subtract(i, "month")
            .format("YYYY"),
        };
        if (this.isMonthly) {
          obj.month = moment(this.endDate)
            .subtract(i, "month")
            .format("MM");
        }
        list.push(obj);
      }
      this.reportPeriodList = list;
    },

    async fetch() {
      try {
        const date = this.isMonthly
          ? `${this.year}${this.month}`
          : `${this.year}`;
        const range = this.isMonthly ? "months" : "years";
        const start = moment(date).format("YYYY-MM-DD");
        const end = moment(date)
          .endOf(range)
          .format("YYYY-MM-DD");
        const payload = {
          vesselId: this.$route.params.vesselId,
          startAt: start,
          endAt: end,
        };
        await this.fetchVoyageRecordsPeriod(payload);
        this.setShowMap(true);
      } catch (error) {
        this.$vs.notify({
          title: "航海記録の取得に失敗しました。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
        this.setShowMap(false);
      }
    },
    async init() {
      try {
        this.$vs.loading();
        await this.fetch();
        if (this.vessels.length === 0) {
          await this.fetchAllVessels();
        }
        await this.initVoyagePeriods();
        await this.fetchReportPeriodList();
        await this.$refs.VesselLiveMap.initMap();
        this.$vs.loading.close();
      } catch (error) {
        console.log(error);
        this.setShowMap(false);
        this.$vs.loading.close();
      }
    },
    async reload() {
      this.RESET_DETAIL_DATA();
      this.reloadCard();
      await this.init();
    },

    reloadCard() {
      //コンポーネント再描写
      this.detailCardKey = this.detailCardKey ? 0 : 1;
    },
  },

  async created() {
    this.RESET_DETAIL_DATA();
  },

  async mounted() {
    await this.init();
    await this.fetchVoyageList({ vesselId: this.vesselId, limit: 9999 });
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "src/assets/scss/aisea/fuel-consumption";

.fuel-consumption-detail-voyage {
  font-family: $aisea-font-family;

  .vessel-name-text {
    font-size: 24px;
  }

  .menu-button {
    @extend .button;
    width: fit-content;
    height: fit-content;
    padding: 6px;
    white-space: nowrap;

    &__disabled {
      @extend .menu-button;
      color: $aisea-mouse-gray;
      cursor: default;

      &:hover {
        background-color: $aisea-pure-white;
      }
    }
  }

  .tab-container {
    height: 600px;
    padding: 10px;
    border: 1px solid $aisea-mouse-gray;
    border-radius: 5px;
  }

  .print {
    display: none;
    page-break-after: always;
  }

  .print-break {
    break-after: page;
  }

  @media print {
    .print {
      display: block;
    }
    .no-print {
      display: none;
    }
  }
}
</style>
